/**
 * Overtime Service
 *
 * Handles all overtime request operations:
 * - Calculating billable hours with rounding rules
 * - Payment cap calculations
 * - Creating overtime requests
 * - Approving/rejecting overtime requests
 */

import { supabase } from '../lib/supabase';

export const overtimeService = {
  /**
   * Calculate billable hours with rounding rules:
   * - Minimum 1 hour guaranteed
   * - After first hour, round up to nearest 0.5 hour
   *
   * @param {number} actualHours - Raw hours worked
   * @returns {number} Billable hours after rounding
   */
  calculateBillableHours(actualHours) {
    if (actualHours <= 0) return 1; // Minimum 1 hour
    if (actualHours <= 1) return 1; // Minimum 1 hour

    // After first hour, round up to nearest 0.5
    const afterFirst = actualHours - 1;
    const roundedAfterFirst = Math.ceil(afterFirst * 2) / 2;
    return 1 + roundedAfterFirst;
  },

  /**
   * Calculate payment with cap applied
   *
   * @param {number} actualHours - Raw hours worked
   * @param {number|null} estimatedHours - Job's estimated hours (null = no cap)
   * @param {number} hourlyRate - Payment rate per hour
   * @returns {Object} Payment breakdown
   */
  calculateCappedPayment(actualHours, estimatedHours, hourlyRate) {
    const billableHours = this.calculateBillableHours(actualHours);

    // If no estimated hours, no cap applies
    const hasEstimate = estimatedHours && estimatedHours > 0;
    const cappedHours = hasEstimate
      ? Math.min(billableHours, estimatedHours)
      : billableHours;

    const overtimeHours = hasEstimate
      ? Math.max(0, billableHours - estimatedHours)
      : 0;

    const basePay = cappedHours * hourlyRate;
    const overtimeAmount = overtimeHours * hourlyRate;

    return {
      actualHours: parseFloat(actualHours.toFixed(2)),
      billableHours: parseFloat(billableHours.toFixed(2)),
      estimatedHours: estimatedHours ? parseFloat(estimatedHours.toFixed(2)) : null,
      cappedHours: parseFloat(cappedHours.toFixed(2)),
      overtimeHours: parseFloat(overtimeHours.toFixed(2)),
      hourlyRate: parseFloat(hourlyRate.toFixed(2)),
      basePay: parseFloat(basePay.toFixed(2)),
      overtimeAmount: parseFloat(overtimeAmount.toFixed(2)),
      totalPotential: parseFloat((basePay + overtimeAmount).toFixed(2)),
      hasOvertime: overtimeHours > 0
    };
  },

  /**
   * Create an overtime request
   *
   * @param {Object} params - Request parameters
   * @returns {Object} Created overtime request
   */
  async createOvertimeRequest({
    jobInstanceId,
    timesheetId,
    contractorId,
    companyId,
    estimatedHours,
    actualHours,
    overtimeHours,
    hourlyRate,
    basePay,
    requestedOvertimeAmount,
    justification
  }) {
    const { data, error } = await supabase
      ?.from('overtime_requests')
      ?.insert({
        job_instance_id: jobInstanceId,
        timesheet_id: timesheetId,
        contractor_id: contractorId,
        company_id: companyId,
        estimated_hours: estimatedHours,
        actual_hours: actualHours,
        overtime_hours: overtimeHours,
        hourly_rate: hourlyRate,
        base_pay: basePay,
        requested_overtime_amount: requestedOvertimeAmount,
        justification,
        status: 'pending'
      })
      ?.select()
      ?.single();

    if (error) throw error;

    // Update timesheet to indicate overtime request exists
    await supabase
      ?.from('timesheets')
      ?.update({ has_overtime_request: true })
      ?.eq('id', timesheetId);

    return data;
  },

  /**
   * Get overtime requests for corporate view
   *
   * @param {string} companyId - Company UUID
   * @param {Object} filters - Optional filters { status: 'pending'|'approved'|etc }
   * @returns {Array} Overtime requests with related data
   */
  async getOvertimeRequests(companyId, filters = {}) {
    let query = supabase
      ?.from('overtime_requests')
      ?.select(`
        *,
        job_instance:job_instances!inner(
          id,
          scheduled_date,
          scheduled_time,
          status,
          job:jobs!inner(
            id,
            job_name,
            job_type,
            location_address,
            service_receiver:service_receivers(id, receiver_name, receiver_type)
          )
        ),
        contractor:user_profiles!overtime_requests_contractor_id_fkey(
          id,
          full_name,
          email
        ),
        reviewer:user_profiles!overtime_requests_reviewed_by_fkey(
          id,
          full_name
        )
      `)
      ?.eq('company_id', companyId)
      ?.order('created_at', { ascending: false });

    if (filters.status && filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  /**
   * Get count of pending overtime requests
   *
   * @param {string} companyId - Company UUID
   * @returns {number} Count of pending requests
   */
  async getPendingOvertimeCount(companyId) {
    const { count, error } = await supabase
      ?.from('overtime_requests')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('company_id', companyId)
      ?.eq('status', 'pending');

    if (error) throw error;
    return count || 0;
  },

  /**
   * Get overtime request for a specific job instance
   *
   * @param {string} jobInstanceId - Job instance UUID
   * @returns {Object|null} Overtime request or null
   */
  async getOvertimeRequestByInstance(jobInstanceId) {
    const { data, error } = await supabase
      ?.from('overtime_requests')
      ?.select('*')
      ?.eq('job_instance_id', jobInstanceId)
      ?.maybeSingle();

    if (error) throw error;
    return data;
  },

  /**
   * Approve overtime request (full or partial)
   *
   * @param {string} requestId - Overtime request UUID
   * @param {number|null} approvedAmount - Amount to approve (null = full amount)
   * @returns {Object} Result with approved amount
   */
  async approveOvertimeRequest(requestId, approvedAmount = null) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get the overtime request
    const { data: request, error: fetchError } = await supabase
      ?.from('overtime_requests')
      ?.select('*, timesheet:timesheets(*)')
      ?.eq('id', requestId)
      ?.single();

    if (fetchError) throw fetchError;
    if (!request) throw new Error('Overtime request not found');

    // Determine approved amount and status
    const finalApprovedAmount = approvedAmount ?? request.requested_overtime_amount;
    const isPartial = finalApprovedAmount < request.requested_overtime_amount && finalApprovedAmount > 0;
    const status = isPartial ? 'partially_approved' : 'approved';

    // Update overtime request
    const { error: requestError } = await supabase
      ?.from('overtime_requests')
      ?.update({
        status,
        approved_overtime_amount: finalApprovedAmount,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString()
      })
      ?.eq('id', requestId);

    if (requestError) throw requestError;

    // Update timesheet with approved overtime amount
    const newTotalAmount = request.base_pay + finalApprovedAmount;
    const { error: timesheetError } = await supabase
      ?.from('timesheets')
      ?.update({
        overtime_amount: finalApprovedAmount,
        amount_earned: newTotalAmount
      })
      ?.eq('id', request.timesheet_id);

    if (timesheetError) throw timesheetError;

    return {
      success: true,
      approvedAmount: finalApprovedAmount,
      newTotalAmount,
      status
    };
  },

  /**
   * Reject overtime request
   *
   * @param {string} requestId - Overtime request UUID
   * @param {string} reason - Rejection reason
   * @returns {Object} Result
   */
  async rejectOvertimeRequest(requestId, reason) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      ?.from('overtime_requests')
      ?.update({
        status: 'rejected',
        approved_overtime_amount: 0,
        rejection_reason: reason,
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString()
      })
      ?.eq('id', requestId);

    if (error) throw error;
    return { success: true };
  }
};

export default overtimeService;
